import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Search, ShoppingBag, Heart } from 'lucide-react'
import { StoreScrollLink } from '@/store/components/StoreScrollLink'
import { StoreHeaderBrand } from './StoreHeaderBrand'
import { useCartStore } from '@/store/stores/cartStore'
import { useWishlistStore } from '@/store/stores/wishlistStore'
import { cn } from '@/lib/utils'
import { useStoreNavActive } from './storeNav'

const nav = [
  { to: '/', label: 'Home', match: 'isHome' as const },
  { to: '/shop', label: 'Shop', match: 'isShop' as const },
  { to: '/shop?sort=new', label: 'New Arrivals', match: 'isNewArrivals' as const },
  { to: '/orders', label: 'Orders', match: 'isOrders' as const },
  { to: '/contact', label: 'Contact', match: 'isContact' as const },
] as const

function navLinkClass(active: boolean) {
  return cn(
    'store-header-nav-link',
    active && 'store-header-nav-link--active'
  )
}

function iconButtonClass(active: boolean) {
  return cn(
    'store-header-icon-btn focus-ring',
    active && 'store-header-icon-btn--active'
  )
}

export function StoreHeader() {
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const active = useStoreNavActive()
  const itemCount = useCartStore((s) => s.itemCount())
  const wishCount = useWishlistStore((s) => s.ids.length)

  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    if (search.trim()) {
      navigate(`/search?q=${encodeURIComponent(search.trim())}`)
    } else {
      navigate('/search')
    }
  }

  return (
    <header className="store-header hidden lg:block" aria-label="Store">
      <div className="store-header-shell">
        <div className="store-container store-header-inner">
          <StoreHeaderBrand />

          <nav className="store-header-nav" aria-label="Primary">
            {nav.map(({ to, label, match }) => {
              const isActive = active[match]
              return (
                <StoreScrollLink
                  key={to}
                  to={to}
                  aria-current={isActive ? 'page' : undefined}
                  className={navLinkClass(isActive)}
                >
                  {label}
                </StoreScrollLink>
              )
            })}
          </nav>

          <div className="store-header-actions">
            <form onSubmit={onSearch} className="store-header-search">
              <div className="relative w-full">
                <Search
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
                  aria-hidden
                />
                <input
                  className="input-luxury store-input pl-10 py-2"
                  placeholder="Search attars..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search products"
                />
              </div>
            </form>

            <Link
              to="/wishlist"
              aria-current={active.isWishlist ? 'page' : undefined}
              className={iconButtonClass(active.isWishlist)}
              aria-label="Wishlist"
            >
              <Heart
                className={cn(
                  'h-5 w-5',
                  active.isWishlist && 'fill-gold-600 text-gold-600'
                )}
                aria-hidden
              />
              {wishCount > 0 ? (
                <span className="store-header-badge">{wishCount > 9 ? '9+' : wishCount}</span>
              ) : null}
            </Link>

            <Link
              to="/cart"
              aria-current={active.isCart ? 'page' : undefined}
              className={iconButtonClass(active.isCart)}
              aria-label="Cart"
            >
              <ShoppingBag className="h-5 w-5" aria-hidden />
              {itemCount > 0 ? (
                <span className="store-header-badge store-header-badge--cart">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              ) : null}
            </Link>
          </div>
        </div>
      </div>
    </header>
  )
}
